import { CodebookEnum } from '@src/components/codebook/view/SuperAdminCodebookListView';
import ITenantConfiguration from '@src/model/tenant/TenantConfiguration';
import StoreService from '@src/service/business/StoreService';
import PublicTenantConfigurationBusinessStore from '@src/service/business/tenant/publicTenantConfigurationBusinessService';
import LemonError from '@src/service/common/LemonError';
import { LangUtils } from '@src/service/util/LangUtils';

/**
 * Supported types of tenant props.
 *
 * Prop "nope" is always negative and is used for testing.
 */
export type TenantPropsType =
  | 'admin_surveys'
  | 'admin_skills'
  | 'admin_emails'
  | 'admin_activities'
  | 'admin_notifications'
  | 'admin_comments'
  | 'admin_workpositions'
  | 'admin_codebooks'
  | 'languages'
  | 'courses'
  | 'scormCourses'
  | 'webinars'
  | 'externalcontents'
  | 'repository'
  | 'usergroups'
  | 'organization_group_courses'
  | 'organizations'
  | 'organizationgraph'
  | 'organization_groups'
  | 'externaleducationapplications'
  | 'externaleducations'
  | 'dashboard'
  | 'notz'
  | 'nope'
  | 'integration_video'
  | 'hrnet_user_import'
  | 'timun_user_import'
  | 'codebook_grade'
  | 'codebook_location'
  | 'codebook_skill_level'
  | 'codebook_user_category'
  | 'codebook_education_category'
  | 'codebook_education_qualification'
  | 'codebook_education_sector'
  | 'codebook_enroll_requirement'
  | 'skillsgap'
  | 'user_profile_edit'
  | 'user_identification_details'
  | 'ldapsync';

/**
 * Static method for checking if tenant prop is enabled. To be used in non-react code (eg. business stores).
 *
 * @see {resolvePropValue} for implementation details
 */
export const isTenantPropsEnabled = (propName: TenantPropsType | TenantPropsType[], state: any) => {
  return resolvePropValues(propName, PublicTenantConfigurationBusinessStore.selectors.getTenantConfiguration(state ?? StoreService.getStore().getState()));
};

/**
 * Resolve tenant property or array of properties.
 *
 * @see {resolvePropValue} for implementation details
 */
export function resolvePropValues(props: TenantPropsType | TenantPropsType[], tenantConfiguration?: ITenantConfiguration): boolean {
  const propArr = ensureArray(props);

  return (
    propArr
      // resolve value for each prop
      .map((prop) => resolvePropValue(prop, tenantConfiguration))
      // ensure all values are true
      .every((value) => value)
  );
}

/**
 * Resolve tenant property state against apropriate config source.
 *
 * Component is a facade for various tenant configuration sources. Currently, only tenant configuration is supported
 * and supported properties are:
 *  - languages - allow locale change
 *  - webinars - are webinars enabled in app
 *  - courses - are courses enabled in app
 *  - externalcontents - are external contents enabled in app
 *  - repository - are repository enabled in app
 *  - usergroups - are user groups enabled in app
 *  - organization - are bussiness profiles aka company enabled in app
 *  - organizationgroups - are organization groups enabled in app
 *  - organizationgraph - is organization graph enabled in app
 *  - externaleducationapplications - are external education applications enabled in app
 *  - dashboard - is dashboard enabled in app
 *  - notz . is notz enabled in app
 *  - nope - always negative, used for debuging
 * - integration_video - enable integration video (currently Vimeo) support, also disables lecture videos uploaded via /files API
 *  - userprofileedit - allow user profile editing
 *
 *  administration links NOTE: (perhaps link it to specific features instead of just hiding it in menu)
 *
 *  - admin_surveys -
 *  - admin_skills -
 *  - admin_emails -
 *  - admin_activities -
 *  - admin_notifications -
 *  - admin_comments -
 *  - admin_workpositions -
 *  - admin_users -
 *  - admin_codebooks -
 */
export function resolvePropValue(propertyName: TenantPropsType, tenantConfiguration?: ITenantConfiguration): boolean {
  // --- administration links
  if ('admin_surveys' === propertyName) {
    return tenantConfiguration?.configuration?.enableSurveys ?? false;
  }
  if ('admin_skills' === propertyName) {
    return tenantConfiguration?.configuration?.enableSkills ?? false;
  }
  if ('skillsgap' === propertyName) {
    return tenantConfiguration?.configuration?.enableSkillsGap ?? false;
  }
  if ('admin_emails' === propertyName) {
    return tenantConfiguration?.configuration?.enableEmails ?? false;
  }
  if ('admin_activities' === propertyName) {
    return tenantConfiguration?.configuration?.enableActivities ?? false;
  }
  if ('admin_notifications' === propertyName) {
    return tenantConfiguration?.configuration?.enableNotifications ?? false;
  }
  if ('admin_comments' === propertyName) {
    return tenantConfiguration?.configuration?.enableComments ?? false;
  }
  if ('admin_workpositions' === propertyName) {
    return tenantConfiguration?.configuration?.enableWorkpositions ?? false;
  }
  if ('admin_codebooks' === propertyName) {
    return tenantConfiguration?.configuration?.adminCodebook?.enableCodebooks ?? false;
  }
  if ('codebook_grade' === propertyName) {
    return tenantConfiguration?.configuration?.adminCodebook?.adminCodebookList?.includes(CodebookEnum.GRADE) ?? false;
  }
  if ('codebook_location' === propertyName) {
    return tenantConfiguration?.configuration?.adminCodebook?.adminCodebookList?.includes(CodebookEnum.LOCATION) ?? false;
  }
  if ('codebook_skill_level' === propertyName) {
    return tenantConfiguration?.configuration?.adminCodebook?.adminCodebookList?.includes(CodebookEnum.SKILL_LEVEL) ?? false;
  }
  if ('codebook_user_category' === propertyName) {
    return tenantConfiguration?.configuration?.adminCodebook?.adminCodebookList?.includes(CodebookEnum.USER_CATEGORY) ?? false;
  }
  if ('codebook_education_category' === propertyName) {
    return tenantConfiguration?.configuration?.adminCodebook?.adminCodebookList?.includes(CodebookEnum.EDUCATION_CATEGORY) ?? false;
  }
  if ('codebook_education_qualification' === propertyName) {
    return tenantConfiguration?.configuration?.adminCodebook?.adminCodebookList?.includes(CodebookEnum.EDUCATION_QUALIFICATION) ?? false;
  }
  if ('codebook_education_sector' === propertyName) {
    return tenantConfiguration?.configuration?.adminCodebook?.adminCodebookList?.includes(CodebookEnum.EDUCATION_SECTOR) ?? false;
  }
  if ('codebook_enroll_requirement' === propertyName) {
    return tenantConfiguration?.configuration?.adminCodebook?.adminCodebookList?.includes(CodebookEnum.ENROLLMENT_REQUIREMENT) ?? false;
  }
  // --- webinars
  if ('webinars' === propertyName) {
    return tenantConfiguration?.configuration?.enableWebinars ?? false;
  }
  // --- courses
  if ('courses' === propertyName) {
    return tenantConfiguration?.configuration?.enableCourses ?? false;
  }
  // --- SCORM courses
  if ('scormCourses' === propertyName) {
    return tenantConfiguration?.configuration?.enableScormCourses ?? false;
  }
  // --- languages
  if ('languages' === propertyName) {
    return tenantConfiguration?.configuration?.enableLanguages ?? false;
  }
  // --- external contents
  if ('externalcontents' === propertyName) {
    return tenantConfiguration?.configuration?.enableExternalContents ?? false;
  }
  // --- repository
  if ('repository' === propertyName) {
    return tenantConfiguration?.configuration?.enableRepository ?? false;
  }
  // --- user groups
  if ('usergroups' === propertyName) {
    return tenantConfiguration?.configuration?.enableUserGroups ?? false;
  }
  // --- organization groups courses
  if ('organization_group_courses' === propertyName) {
    return tenantConfiguration?.configuration?.enableOrganizationGroupCourses ?? false;
  }
  // --- organizations are business profile (aka company)
  if ('organizations' === propertyName) {
    return tenantConfiguration?.configuration?.enableOrganizations ?? false;
  }
  // --- organization graph
  if ('organizationgraph' === propertyName) {
    return tenantConfiguration?.configuration?.enableOrganizationGraph ?? false;
  }
  // --- organization_group
  if ('organization_groups' === propertyName) {
    return tenantConfiguration?.configuration?.enableOrganizationGroups ?? false;
  }

  // --- external education applications
  if ('externaleducationapplications' === propertyName) {
    return tenantConfiguration?.configuration?.enableExternalEducationApplications ?? false;
  }
  // --- external education
  if ('externaleducations' === propertyName) {
    return tenantConfiguration?.configuration?.enableExternalEducations ?? false;
  }
  // --- integration video
  if ('integration_video' === propertyName) {
    return tenantConfiguration?.configuration?.enableIntegrationVideo ?? false;
  }
  // --- dashboard
  if ('dashboard' === propertyName) {
    return tenantConfiguration?.configuration?.enableDashboard ?? false;
  }
  // --- notz
  if ('notz' === propertyName) {
    return tenantConfiguration?.configuration?.enableNotz ?? false;
  }
  // --- hrnet user import
  if ('hrnet_user_import' === propertyName) {
    return tenantConfiguration?.configuration?.enableHrnetUserImport ?? false;
  }
  // --- timun user import
  if ('timun_user_import' === propertyName) {
    return tenantConfiguration?.configuration?.enableTimunUserImport ?? false;
  }
  // --- user profile edit
  if ('user_profile_edit' === propertyName) {
    return tenantConfiguration?.configuration?.enableUserProfileEdit ?? false;
  }
  // --- user identification details edit
  if ('user_identification_details' === propertyName) {
    return tenantConfiguration?.configuration?.enableUserIdentificationDetailsEdit ?? false;
  }
  // --- ldap sync
  if ('ldapsync' === propertyName) {
    return tenantConfiguration?.configuration?.enableLDAP ?? false;
  }
  // --- nope - always false value (for testing)
  else if ('nope' === propertyName) {
    return false;
  }
  // fallback
  else {
    throw new LemonError(`Unhandled tenant property ${propertyName}`);
  }
}

// ----- private

function ensureArray<T>(prop: T | T[]): T[] {
  return LangUtils.isArray(prop) ? prop : [prop];
}
